import { Grid, Box, Typography,capitalize } from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import { EmptyContainer } from 'shared-components/EmptyContainer';
import { OutlineButton } from 'shared-components/Buttons/Outline';
import { LineChart,
  AgeGraph,
  StatesHeatMapGraph,
  DistanceTraveledToSeeClinician,
} from 'components/RegistryDashboard/Graphs';
import { HeatMapWrapper, Card, Percentage } from './styles';

import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { position } from 'utils/string';
import {
  useGetLiverScoreQuery,
  useGetAneurysmsScreeningQuery,
  useGetFamilyHistoryScoreQuery,
  useGetStudyParticipationQuery,
  useGetFatigueAndAbdomenSizeScoreGraphQuery,
  useGetRegistryCountQuery,
} from 'store/api/pkd/endpoints/graphs';
import { PageLoader, PageLoaderWrapper } from 'shared-components/Loaders';
import DefaultWrapper from 'shared-components/DefaultWrapper';
import { useGetProfileParticipantTasksQuery, useGetProfileParticipantTaskQuery } from 'store/api/pkd/endpoints/participantTask';
import { TaskTitles } from 'utils/enum';

const NoDataYet = ({ ADPKDIS, theme }) => {
  return (
    <Box
      sx={{ paddingBottom: 6 }}>
      <EmptyContainer
        inline
        title='No data yet'
        description={ADPKDIS && ADPKDIS?.length !== 0 && 'Fill out the ADPKD-IS module'}
        hideCta={!ADPKDIS || ADPKDIS?.length === 0}
        renderCta={
          <Link
            to={`/questionnaire/${ADPKDIS?.[0]?.id}`}>
            <OutlineButton
              width='100%'
              fontFamily='Montserrat'
              color={theme.palette.primary.blue1}
              borderColor={theme.palette.primary.blue1}
              text='Fill out the ADPKD-IS module'
            />
          </Link>
        }
      />
    </Box>
  );
};

const BotheredLineChart = ({ fatigueAndAmbitionScore, ADPKDIS, theme }) => {
  if (
    !fatigueAndAmbitionScore?.abdomnenSizeScore.length ||
    !fatigueAndAmbitionScore?.date.length
  ) {
    return <NoDataYet
      ADPKDIS={ADPKDIS} theme={theme}
    />;
  }
  return (
    <LineChart
      date={fatigueAndAmbitionScore?.date}
      graphData={fatigueAndAmbitionScore?.abdomnenSizeScore}
      id='bothered-line-chart'
      grid={6}
      height='300px'
      width='550px'
    />
  );
};

const FeelingLineChart = ({ fatigueAndAmbitionScore, ADPKDIS, theme }) => {
  if (
    !fatigueAndAmbitionScore?.fatigueScore.length ||
    !fatigueAndAmbitionScore?.date.length
  ) {
    return <NoDataYet
      ADPKDIS={ADPKDIS} theme={theme}
    />;
  }

  return (
    <LineChart
      date={fatigueAndAmbitionScore?.date}
      graphData={fatigueAndAmbitionScore?.fatigueScore}
      id='feeling-line-chart'
      grid={6}
      height='300px'
      width='550px'
    />
  );
};

const parseFatigueAndAbdomenSizeScore = (data) => {
  return data?.reduce(
    (acc, { reportedDt, abdomnenSizeScore, fatigueScore }) => {
      const date = new Date(reportedDt).toLocaleDateString('en-US', {
        month : 'short',
        year  : 'numeric',
      });

      acc.date.push(date);
      acc.abdomnenSizeScore.push(abdomnenSizeScore);
      acc.fatigueScore.push(fatigueScore);

      return acc;
    },
    {
      date              : [],
      abdomnenSizeScore : [],
      fatigueScore      : [],
    }
  );
};

export const RegistryDashboard = () => {
  const theme = useTheme();
  const { isPersonLoading, isPersonAttributesFetching, person, personId, personAccountProfileId } =
  useGetPersonAndAttributes();

  const { data: registryCount, isLoading: isRegistryCountLoading } =
    useGetRegistryCountQuery({ personId: personId }, { skip: !person });

  const { data: familyHistory, isLoading: isFamilyHistoryLoading } =
    useGetFamilyHistoryScoreQuery();

  const familyMembers = familyHistory ? familyHistory?.[0] : {};

  const { data: fatigueGraph, isFetching: isFatigueGraphLoading } =
    useGetFatigueAndAbdomenSizeScoreGraphQuery({ personId });

  const { data: studyParticipation, isFetching: isStudyParticipationLoading } =
    useGetStudyParticipationQuery();

  const { data: aneurysmsScreening, isFetching: isAneurysmsScreeningLoading } =
    useGetAneurysmsScreeningQuery();

  const { data: liveScore, isFetching: isLiveScoreLoading } =
    useGetLiverScoreQuery();

  const { participantTask, isParticipantTaskLoading } =
    useGetProfileParticipantTasksQuery(
      { personAccountProfileId },
      { skip             : !personAccountProfileId ,
        selectFromResult : ({ data = [], isFetching }) => ({
          participantTask          : data?.find(item => item.taskTitle === TaskTitles.ADPKDIS),
          isParticipantTaskLoading : isFetching,
        }),
      }
    );
   
  const { data:ADPKDIS, isLoading:isADPKDISLoading } =
    useGetProfileParticipantTaskQuery(
      { personAccountProfileId, participantTaskId: participantTask?.id },
      { skip: !participantTask }
    );

  const isLoading =
    isFatigueGraphLoading ||
    isPersonLoading ||
    isRegistryCountLoading ||
    isADPKDISLoading;
  const fatigueAndAmbitionScore = parseFatigueAndAbdomenSizeScore(fatigueGraph);

  if (isLoading) {
    return (
      <PageLoaderWrapper>
        <PageLoader />
      </PageLoaderWrapper>
    );
  }

  return (
    <DefaultWrapper
      crumbs={[{ title: 'Registry Dashboard', isActive: true }]}
      margin={{ xs: '0 0 78px 0', lg: '10px 78px 78px 78px' }}
      sx={{
        display       : 'flex',
        flexDirection : 'column',
      }}>
      <Box
        sx={{
          display       : 'flex',
          flexDirection : 'column',
          margin        : '0 auto',
          padding       : '15px',
        }}>
        <Box
          sx={{
            display        : 'flex',
            justifyContent : 'space-between',
            flexDirection  : { xs: 'column', lg: 'row' },
            gap            : { xs: '1rem', lg: '3rem', xl: '6rem' },
          }}>
          <Box
            sx={{
              display       : 'flex',
              flexDirection : 'column',
              minWidth      : '300px',
              maxWidth      : { lg: '600px' },
            }}>
            <Typography
              fontFamily='Montserrat' margin='25px 0' fontSize='32px'>
              Registry Dashboard
            </Typography>
            <Typography
              variant='body1'
              fontFamily='Montserrat'
              lineHeight='30px'>
              We&apos;re excited to share some data with you that resulted directly
              from your answers to our questions. Below you can find how your
              answers compare with other participants in the Registry. Check
              back after each survey for updates.
              <br />
              <br />
              For more resources, community support and updates on PKD research,
              visit the PKD Foundation website. <br />
              <br /> You are the{' '}
              {!isLoading && registryCount?.participantPosition
                ? position(registryCount?.participantPosition)
                : '...'}{' '}
              person to join the ADPKD Registry! Thanks to people like you we
              are now up to{' '}
              {!isLoading ? registryCount?.totalParticipants : '...'}{' '}
              participants.
            </Typography>
          </Box>
          <HeatMapWrapper
            sx={{
              width    : 'unset',
              padding  : { xs: '10px', md: '25px 40px' },
              minWidth : '50%',
            }}>
            <Typography
              variant='h5'
              fontFamily='Montserrat'
              sx={{
                wordBreak : 'break-word',
                padding   : { xs: '10px 20px 0px 20px', lg: 0 },
              }}>
              US Heatmap
            </Typography>
            <Box
              display='flex' justifyContent='center'>
              <StatesHeatMapGraph
                id='registry-heat-map'
                width='600px'
                height='400px'
              />
            </Box>
          </HeatMapWrapper>
        </Box>
        <Grid
          container
          spacing={1}
          marginTop={{ xs: 2, md: 8 }}
          direction={{ xs: 'column', md: 'row' }}
          sx={{
            justifyContent : { lg: 'space-between' },
            flexWrap       : { xs: 'wrap', md: 'nowrap' },
          }}>
          <Grid
            item md={6} lg={3}>
            <Card
              sx={{
                height    : '100%',
                maxHeight : '510px',
                minHeight : '230px',
                padding   : { xs: '10px', lg: '25px 40px' },
              }}>
              <Typography
                variant='h5'
                fontFamily='Montserrat'
                sx={{
                  wordBreak : 'break-word',
                  padding   : { xs: '10px 20px 0px 20px', lg: 0 },
                }}>
                Age
              </Typography>
              <Box
                display='flex' justifyContent='center'>
                <AgeGraph
                  id='registry-pie-chart'
                  width='230px'
                  height='300px'
                  center={['50%', '30%']}
                  legendOptions={{
                    bottom : '0%',
                    orient : 'horizontal',
                  }}
                />
              </Box>
            </Card>
          </Grid>
          <Grid
            item md={6} lg={3}>
            <Card
              sx={{
                height    : '100%',
                maxHeight : '510px',
                minHeight : '230px',
                padding   : { xs: '10px', lg: '25px 40px' },
              }}>
              <Typography
                variant='h5'
                fontFamily='Montserrat'
                marginBottom='10px'>
                Distance traveled to see PKD Clinician
              </Typography>
              <DistanceTraveledToSeeClinician
                id='distance-bar-chart'
                width='230px'
                height='250px'
              />
            </Card>
          </Grid>
          <Grid
            item md={12} lg={6}>
            <Card
              sx={{
                height    : '100%',
                maxHeight : '510px',
                minHeight : '230px',
                padding   : { xs: '10px', lg: '25px 40px' },
              }}>
              <Typography
                variant='h5'
                fontFamily='Montserrat'
                sx={{
                  wordBreak : 'break-word',
                  padding   : { xs: '10px 20px 0px 20px', lg: 0 },
                }}>
                Participants reporting PKD in these family members
              </Typography>
              {isFamilyHistoryLoading
                ? (
                  <PageLoader />
                )
                : (
                  <Box
                    sx={{
                      marginTop           : '4rem',
                      display             : ' grid',
                      gridTemplateColumns : 'repeat(auto-fit, minmax(129px, 1fr))',
                      gridGap             : '15px',
                    }}>
                    {Object.keys(familyMembers).map((member, index) => (
                      <Box
                        key={`${index}_${member}`}
                        sx={{ marginX: 'auto' }}>
                        <Typography
                          color={theme.palette.primary.blue1}
                          fontFamily='Montserrat'
                          fontSize='35px'>
                          {familyMembers[member]}%
                        </Typography>
                        <Typography
                          variant='body1' fontFamily='Montserrat'>
                          {capitalize(member)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
            </Card>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={1}
          marginTop={{ xs: 2, md: 8 }}
          direction={{ xs: 'column', md: 'row' }}>
          <Grid
            item md={4}>
            <Card
              sx={{
                height    : '100%',
                maxHeight : '100%',
                minHeight : '230px',
                padding   : { xs: '10px', md: '25px 40px' },
              }}>
              <Typography
                variant='h5'
                fontFamily='Montserrat'
                sx={{
                  wordBreak : 'break-word',
                  padding   : { xs: '10px 20px 0px 20px', lg: 0 },
                }}>
                Participated in a clinical study
              </Typography>
              {isStudyParticipationLoading
                ? (
                  <PageLoader />
                )
                : (
                  <Percentage>
                    {studyParticipation?.[0]?.participatedinpkd}%
                  </Percentage>
                )}
            </Card>
          </Grid>

          <Grid
            item md={4}>
            <Card
              sx={{
                height    : '100%',
                maxHeight : '100%',
                minHeight : '230px',
                padding   : { xs: '10px', md: '25px 40px' },
              }}>
              <Typography
                variant='h5'
                fontFamily='Montserrat'
                sx={{
                  wordBreak : 'break-word',
                  padding   : { xs: '10px 20px 0px 20px', lg: 0 },
                }}>
                Told by doctor their liver is larger than average
              </Typography>
              {isLiveScoreLoading
                ? (
                  <PageLoader />
                )
                : (
                  <Percentage>{liveScore?.[0]?.largeliver}%</Percentage>
                )}
            </Card>
          </Grid>

          <Grid
            item md={4}>
            <Card
              sx={{
                height    : '100%',
                maxHeight : '100%',
                minHeight : '230px',
                padding   : { xs: '10px', md: '25px 40px' },
              }}>
              <Typography
                variant='h5'
                fontFamily='Montserrat'
                sx={{
                  wordBreak : 'break-word',
                  padding   : { xs: '10px 20px 0px 20px', lg: 0 },
                }}>
                Told should be screened for intracranial aneurysms
              </Typography>
              {isAneurysmsScreeningLoading
                ? (
                  <PageLoader />
                )
                : (
                  <Percentage>
                    {aneurysmsScreening?.[0]?.pkdScreenedforaneurysms}%
                  </Percentage>
                )}
            </Card>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={1}
          marginTop={{ xs: 2, md: 8 }}
          direction={{ xs: 'column', md: 'row' }}>
          <Grid
            item md={6}>
            <Card
              sx={{
                height         : '100%',
                maxHeight      : '100%',
                minHeight      : '230px',
                padding        : { xs: '10px', md: '25px 40px' },
                flex           : 1,
                display        : 'flex',
                justifyContent : 'space-between',
                flexDirection  : 'column',
              }}>
              <Typography
                variant='h5'
                fontFamily='Montserrat'
                sx={{
                  wordBreak : 'break-word',
                  padding   : { xs: '10px 20px 0px 20px', lg: 0 },
                }}>
                Bothered by the size and/or shape of your abdomen
              </Typography>
              <BotheredLineChart
                fatigueAndAmbitionScore={fatigueAndAmbitionScore}
                ADPKDIS={ADPKDIS}
                theme={theme}
              />
            </Card>
          </Grid>
          <Grid
            item md={6} sx={{ display: 'flex' }}>
            <Card
              sx={{
                height         : '100%',
                maxHeight      : '100%',
                minHeight      : '230px',
                padding        : { xs: '10px', md: '25px 40px' },
                flex           : 1,
                display        : 'flex',
                justifyContent : 'space-between',
                flexDirection  : 'column',
              }}>
              <Typography
                variant='h5'
                fontFamily='Montserrat'
                sx={{
                  wordBreak : 'break-word',
                  padding   : { xs: '10px 20px 0px 20px', lg: 0 },
                }}>
                Feeling exhausted or fatigued
              </Typography>
              <FeelingLineChart
                fatigueAndAmbitionScore={fatigueAndAmbitionScore}
                ADPKDIS={ADPKDIS}
                theme={theme}
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DefaultWrapper>
  );
};
